import { ContactInformationData } from './ReviewerProfile.Overview.ContactInformation';
import { LicenseInformationData } from './ReviewerProfile.Overview.LicenseInformation';
import { RatesData } from './ReviewerProfile.Overview.Rates';
import { isValidEmail } from '../../../Utilities/validators';

// Mirrors the required fields enforced by ReviewerProfile.Overview.ContactInformation's
// Yup schema and the `required` markers on ReviewerProfile.Overview.LicenseInformation's fields.
export const isContactInformationComplete = (data?: ContactInformationData): boolean =>
  !!data?.firstName && !!data?.lastName && isValidEmail(data?.email);

export const isLicenseInformationComplete = (data?: LicenseInformationData): boolean =>
  !!data?.primaryBoard
  && !!data?.primarySpecialty
  && !!data?.licenseType
  && data?.activePractice != null
  && data?.activePracticeHoursPerWeek != null;

// Reviewer document type names, as returned by GET /reviewers/documentTypes and by
// documentType.name on GET /reviewers/{id}/documents. ReviewerDocumentType is a DB-seeded
// lookup table on the backend (not a numeric enum), so these are matched by name.
export const REVIEWER_DOCUMENT_TYPE_CONTRACT = 'Contract';
export const REVIEWER_DOCUMENT_TYPE_ADDENDUM = 'Addendum';
export const REVIEWER_DOCUMENT_TYPE_FEE_SCHEDULE = 'Fee Schedule';

// Reviewer type values mirror the backend ReviewerType enumeration (see also the equivalent
// local constants in ReviewerProfile.Overview.tsx and ReviewerProfile.Overview.Rates.tsx).
const REVIEWER_TYPE_PEER_REVIEW = 1;
const REVIEWER_TYPE_IME = 2;
const REVIEWER_TYPE_PEER_REVIEW_IME = 3;

const isPeerReviewType = (reviewerTypeValue?: number): boolean =>
  reviewerTypeValue === REVIEWER_TYPE_PEER_REVIEW || reviewerTypeValue === REVIEWER_TYPE_PEER_REVIEW_IME;

// Cosigner (isCosigner) is a Yes/No field on the Rates card, not Contact/License Information.
// Only required for Peer Review reviewer types — UpdateRatesCommandHandler force-nulls IsCosigner
// for IME-only reviewers, so requiring it for them would make this permanently unsatisfiable.
// Reviewer type is edited on the Rates card, and only the Rates data is refetched after that save.
// Prefer it so required-document checks don't run against a stale type until a page reload
// (STRAD2-2671 / TC-191). License Information carries the same value as a fallback.
const resolveReviewerType = (ratesData?: RatesData, licenseData?: LicenseInformationData): number | undefined =>
  ratesData?.reviewerTypeValue ?? licenseData?.reviewerTypeValue;

export const isRatesInformationComplete = (data?: RatesData): boolean =>
  !isPeerReviewType(data?.reviewerTypeValue) || data?.isCosigner != null;

// Peer Review: Contract, Addendum. IME: Fee Schedule, Contract, Addendum. Peer Review + IME: all three.
const REQUIRED_DOCUMENT_TYPES_BY_REVIEWER_TYPE: Record<number, string[]> = {
  [REVIEWER_TYPE_PEER_REVIEW]: [REVIEWER_DOCUMENT_TYPE_CONTRACT, REVIEWER_DOCUMENT_TYPE_ADDENDUM],
  [REVIEWER_TYPE_IME]: [REVIEWER_DOCUMENT_TYPE_FEE_SCHEDULE, REVIEWER_DOCUMENT_TYPE_CONTRACT, REVIEWER_DOCUMENT_TYPE_ADDENDUM],
  [REVIEWER_TYPE_PEER_REVIEW_IME]: [REVIEWER_DOCUMENT_TYPE_FEE_SCHEDULE, REVIEWER_DOCUMENT_TYPE_CONTRACT, REVIEWER_DOCUMENT_TYPE_ADDENDUM],
};

// Mirrors ReviewerProfileCompletenessValidator.cs's GetMissingDocumentTypesAsync. Returns the
// required types not yet uploaded (empty = complete), or null when the reviewer type itself isn't
// set, so completeness can't even be evaluated.
export const getMissingDocumentTypes = (
  reviewerTypeValue?: number,
  uploadedDocumentTypeNames?: string[]
): string[] | null => {
  if (reviewerTypeValue == null) return null;
  const requiredTypes = REQUIRED_DOCUMENT_TYPES_BY_REVIEWER_TYPE[reviewerTypeValue];
  if (!requiredTypes) return null;

  const uploadedTypes = new Set(uploadedDocumentTypeNames ?? []);
  return requiredTypes.filter((type) => !uploadedTypes.has(type));
};

// At least one uploaded document of each required type above (per the reviewer's type) must be present.
export const isRequiredDocumentsComplete = (
  reviewerTypeValue?: number,
  uploadedDocumentTypeNames?: string[]
): boolean => {
  const missingTypes = getMissingDocumentTypes(reviewerTypeValue, uploadedDocumentTypeNames);
  return missingTypes != null && missingTypes.length === 0;
};

// These labels must match the section titles in ReviewerProfile.Overview.tsx (Contact
// Information, License Information, Rates) — FormSection derives its DOM id from that title
// (spaces stripped, lowercased), which is what FormErrorSection's scroll-to-section relies on.
// Documents isn't a FormSection card — it's a separate profile tab.
export const REVIEWER_PROFILE_SECTION_CONTACT_INFORMATION = 'Contact Information';
export const REVIEWER_PROFILE_SECTION_LICENSE_INFORMATION = 'License Information';
export const REVIEWER_PROFILE_SECTION_RATES = 'Rates';
export const REVIEWER_PROFILE_SECTION_DOCUMENTS = 'Documents';

export const getReviewerProfileMissingSections = (
  contactData?: ContactInformationData,
  licenseData?: LicenseInformationData,
  ratesData?: RatesData,
  uploadedDocumentTypeNames?: string[]
): string[] => {
  const missing: string[] = [];
  if (!isContactInformationComplete(contactData)) missing.push(REVIEWER_PROFILE_SECTION_CONTACT_INFORMATION);
  if (!isLicenseInformationComplete(licenseData)) missing.push(REVIEWER_PROFILE_SECTION_LICENSE_INFORMATION);
  if (!isRatesInformationComplete(ratesData)) missing.push(REVIEWER_PROFILE_SECTION_RATES);
  if (!isRequiredDocumentsComplete(resolveReviewerType(ratesData, licenseData), uploadedDocumentTypeNames)) {
    missing.push(REVIEWER_PROFILE_SECTION_DOCUMENTS);
  }
  return missing;
};

// Shared by both "Mark Profile Completed" and "Invite" — both require the full required-field,
// Cosigner, and per-reviewer-type document set to be complete.
export const isReviewerProfileRequiredFieldsComplete = (
  contactData?: ContactInformationData,
  licenseData?: LicenseInformationData,
  ratesData?: RatesData,
  uploadedDocumentTypeNames?: string[]
): boolean =>
  getReviewerProfileMissingSections(contactData, licenseData, ratesData, uploadedDocumentTypeNames).length === 0;

// The backend runs this same check (ReviewerProfileCompletenessValidator) and returns failures
// keyed by stable camelCase wire names — NOT these display labels — because Startup.cs's
// CamelCasePropertyNamesContractResolver also camelCases dictionary keys, which would silently
// mangle a display-style key like "Contact Information". This maps the wire keys back to the
// same labels FormErrorSection already renders for the client-side check.
const BACKEND_MISSING_SECTION_KEY_TO_LABEL: Record<string, string> = {
  contactInformation: REVIEWER_PROFILE_SECTION_CONTACT_INFORMATION,
  licenseInformation: REVIEWER_PROFILE_SECTION_LICENSE_INFORMATION,
  rates: REVIEWER_PROFILE_SECTION_RATES,
  documents: REVIEWER_PROFILE_SECTION_DOCUMENTS,
};

export type MissingSectionError = { section: string; message: string };

// Mirrors ReviewerProfileCompletenessValidator.cs's duplicate-email check EXACTLY. Unlike every
// other message below, this one is backend-only — the frontend can't query other users' emails —
// so pruneResolvedMissingSections special-cases it instead of relying on getMissingFieldMessages.
export const EMAIL_ALREADY_IN_USE_MESSAGE = 'This email address is already being used by another user.';

// Mirrors ReviewerProfileCompletenessValidator.cs's per-field messages EXACTLY (string-for-string)
// — not just which sections are incomplete, but which specific field messages the backend would
// currently produce. This is what lets the banner drop a single resolved field (e.g. "Primary
// board is required.") without waiting for every other field in the same section to also clear.
// If a message's wording changes on one side, update the other — pruning matches on exact text.
export const getMissingFieldMessages = (
  contactData?: ContactInformationData,
  licenseData?: LicenseInformationData,
  ratesData?: RatesData,
  uploadedDocumentTypeNames?: string[]
): MissingSectionError[] => {
  const messages: MissingSectionError[] = [];

  if (!contactData?.firstName) {
    messages.push({ section: REVIEWER_PROFILE_SECTION_CONTACT_INFORMATION, message: 'First name is required.' });
  }
  if (!contactData?.lastName) {
    messages.push({ section: REVIEWER_PROFILE_SECTION_CONTACT_INFORMATION, message: 'Last name is required.' });
  }
  if (!contactData?.email) {
    messages.push({ section: REVIEWER_PROFILE_SECTION_CONTACT_INFORMATION, message: 'Email is required.' });
  } else if (!isValidEmail(contactData.email)) {
    messages.push({ section: REVIEWER_PROFILE_SECTION_CONTACT_INFORMATION, message: 'Email is not valid.' });
  }

  if (!licenseData?.primaryBoard) {
    messages.push({ section: REVIEWER_PROFILE_SECTION_LICENSE_INFORMATION, message: 'Primary board is required.' });
  }
  if (!licenseData?.primarySpecialty) {
    messages.push({ section: REVIEWER_PROFILE_SECTION_LICENSE_INFORMATION, message: 'Primary specialty is required.' });
  }
  if (!licenseData?.licenseType) {
    messages.push({ section: REVIEWER_PROFILE_SECTION_LICENSE_INFORMATION, message: 'License type is required.' });
  }
  if (licenseData?.activePractice == null) {
    messages.push({ section: REVIEWER_PROFILE_SECTION_LICENSE_INFORMATION, message: 'Active practice is required.' });
  }
  if (licenseData?.activePracticeHoursPerWeek == null) {
    messages.push({
      section: REVIEWER_PROFILE_SECTION_LICENSE_INFORMATION,
      message: 'Active practice hours per week is required.',
    });
  }

  if (isPeerReviewType(ratesData?.reviewerTypeValue) && ratesData?.isCosigner == null) {
    messages.push({ section: REVIEWER_PROFILE_SECTION_RATES, message: 'Cosigner selection is required.' });
  }

  const missingDocumentTypes = getMissingDocumentTypes(resolveReviewerType(ratesData, licenseData), uploadedDocumentTypeNames);
  if (missingDocumentTypes == null) {
    messages.push({
      section: REVIEWER_PROFILE_SECTION_DOCUMENTS,
      message: 'Reviewer type must be set before required documents can be determined.',
    });
  } else {
    missingDocumentTypes.forEach((type) => {
      messages.push({ section: REVIEWER_PROFILE_SECTION_DOCUMENTS, message: `${type} is required.` });
    });
  }

  return messages;
};

// Extracts missing-section labels AND their FluentValidation messages from a failed axios-style
// response (resp.response.data.errors), matching the { errors: { key: [messages] } } shape used
// across this app's FluentValidation-backed endpoints. Returns null when the response isn't this
// kind of validation error.
//
// A single section key (e.g. "documents") can carry multiple messages — the backend adds one
// failure per missing document type. Each message becomes its own entry here so the banner lists
// every missing item individually instead of collapsing them into one line.
export const getMissingSectionsFromErrorResponse = (resp: any): MissingSectionError[] | null => {
  const errors = resp?.response?.data?.errors;
  if (!errors || typeof errors !== 'object') return null;

  // reduce, not flatMap — this project targets es5 with lib es6, and flatMap is ES2019.
  const sections = Object.entries(errors as Record<string, unknown>).reduce<MissingSectionError[]>(
    (acc, [key, messages]: [string, unknown]) => {
      const section = BACKEND_MISSING_SECTION_KEY_TO_LABEL[key];
      if (!section) return acc;
      const messageList = Array.isArray(messages) ? messages : [String(messages)];
      messageList.forEach((message) => acc.push({ section, message: String(message) }));
      return acc;
    },
    []
  );

  return sections.length > 0 ? sections : null;
};

const missingFieldKey = (error: MissingSectionError): string => `${error.section} ${error.message}`;

// Removes entries for individual fields that are now resolved client-side, without ever adding new
// ones — only an actual backend validation attempt should surface a NEW error. Matches on exact
// (section, message) pairs via getMissingFieldMessages, not just "is the whole section done",
// so fixing one of several missing fields in a section drops only that one line from the banner.
//
// EMAIL_ALREADY_IN_USE_MESSAGE is handled separately since it has no client-side check to match
// against: it's kept as long as the email hasn't changed since the failed attempt that produced it
// (emailAtLastValidation), and dropped once the user edits the email — the next save/invite attempt
// re-validates it against the backend for real.
export const pruneResolvedMissingSections = (
  currentErrors: MissingSectionError[] | null,
  contactData?: ContactInformationData,
  licenseData?: LicenseInformationData,
  ratesData?: RatesData,
  uploadedDocumentTypeNames?: string[],
  emailAtLastValidation?: string
): MissingSectionError[] | null => {
  if (!currentErrors || currentErrors.length === 0) return currentErrors;

  const stillMissing = new Set(
    getMissingFieldMessages(contactData, licenseData, ratesData, uploadedDocumentTypeNames).map(missingFieldKey)
  );
  const emailUnchanged = contactData?.email === emailAtLastValidation;

  const remaining = currentErrors.filter((error) =>
    error.message === EMAIL_ALREADY_IN_USE_MESSAGE ? emailUnchanged : stillMissing.has(missingFieldKey(error))
  );
  if (remaining.length === currentErrors.length) return currentErrors;
  return remaining.length > 0 ? remaining : null;
};
